import React, { useState, useMemo } from 'react';
import { Card } from '../../ui/Card';
import { Tooltip } from '../../ui/Tooltip';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Select } from '../../ui/Select';
import { 
  Coffee, Droplets, Package, Sparkles, 
  ShoppingCart, TrendingUp, Calendar, MapPin 
} from 'lucide-react';
import { colors, agentThemes, spacing, borderRadius, shadows } from '../../../constants/theme';

// 秋の調味料クロスMD商品データ型
interface SeasonalProduct {
  小分類: string;
  メーカー名: string;
  商品名: string;
  サイズ: string;
  価格: number;
  元棚段番号: number;
  コーナー: string;
  シーズン: string;
  週: string;
  調味料テーマ: string[];
  役割: string;
  推奨棚段番号: number;
  推奨フェイス数: number;
  クロスMD: string[];
  POP: string;
  配置: string;
}

// カテゴリー別の色設定（秋バージョン）
const AUTUMN_COLORS = {
  'お茶・紅茶': {
    base: '#2D5016',
    accent: '#8B6F47',
    light: '#A8C09A',
    商品別: {
      '綾鷹': { bg: '#2D5016', text: 'white' },
      '綾鷹濃い緑茶': { bg: '#1B3409', text: 'white' },
      '爽健美茶': { bg: '#5B8C3A', text: 'white' },
      'やかんの麦茶': { bg: '#8B6F47', text: 'white' },
      '伊右衛門': { bg: '#4A7C59', text: 'white' },
      'お～いお茶': { bg: '#5F8A3A', text: 'white' },
      '午後の紅茶': { bg: '#C85A17', text: 'white' },
    }
  },
  '水': {
    base: '#87CEEB',
    accent: '#4682B4',
    light: '#E0F7FA',
    商品別: {
      'いろはす': { bg: '#87CEEB', text: 'black' },
      'いろはすマスカット': { bg: '#90EE90', text: 'black' },
      'GREEN DAKARA': { bg: '#90EE90', text: 'black' },
      '天然水': { bg: '#4682B4', text: 'white' },
      '天然水SPARKLING': { bg: '#00CED1', text: 'white' },
      'ウィルキンソン炭酸水': { bg: '#1E90FF', text: 'white' },
    }
  },
  'スポーツドリンク': {
    base: '#00BFFF',
    accent: '#4169E1',
    light: '#E6F7FF',
    商品別: {
      'アクエリアス': { bg: '#00BFFF', text: 'white' },
      'ポカリスエット': { bg: '#87CEFA', text: 'black' },
      'カルピスソーダ': { bg: '#4169E1', text: 'white' },
    }
  },
  '炭酸飲料': {
    base: '#8B0000',
    accent: '#FF8C00',
    light: '#FFE4E1',
    商品別: {
      'コカ・コーラ': { bg: '#8B0000', text: 'white' },
      'コカ･コーラゼロ': { bg: '#2F4F4F', text: 'white' },
      'ファンタオレンジ': { bg: '#FF8C00', text: 'white' },
      'ファンタグレープ': { bg: '#8B008B', text: 'white' },
      'カナダドライジンジャーエール': { bg: '#FFD700', text: 'black' },
    }
  }
};

// 調味料テーマの色設定
const THEME_COLORS = {
  '鍋': '#D2691E',
  'おでん': '#8B4513',
  '和惣菜': '#556B2F',
  '焼き魚': '#4682B4',
  '根菜': '#A0522D',
  'きのこ': '#8B7355',
  '洋風煮込み': '#B22222',
  'パスタソース': '#DC143C',
  'スイーツソース': '#FF69B4',
  '焼き菓子': '#F4A460',
  'BBQソース': '#8B0000',
  '照り焼きタレ': '#654321',
  'レモン果汁': '#FFD700',
  'ビネガー': '#DAA520',
  'シチュー': '#FFA500',
  'カレー': '#FF8C00',
  'デザートソース': '#FF1493',
  'ヘルシー調味': '#90EE90',
  'マーマレード': '#FF8C00',
  '砂糖': '#FFDEAD',
  'バルサミコ酢': '#4B0082',
  'フルーツソース': '#FF69B4',
  '生姜': '#DEB887',
  'から揚げ粉': '#F5DEB3',
};

// 配置位置のアイコン
const PLACEMENT_ICONS = {
  'ゴンドラ中央': '🎯',
  'エンド': '📍',
  'サイドキャップ': '🔖',
  '下段': '📦',
  '下段端': '📌',
  '下段中央': '📍',
};

interface SeasonalCrossMDVisualizerProps {
  products?: SeasonalProduct[];
  viewMode?: 'seasonal' | 'category' | 'crossmd';
  onProductClick?: (product: SeasonalProduct) => void;
}

const SeasonalCrossMDVisualizer: React.FC<SeasonalCrossMDVisualizerProps> = ({
  products = [],
  viewMode: initialViewMode = 'seasonal',
  onProductClick
}) => {
  const [selectedProduct, setSelectedProduct] = useState<SeasonalProduct | null>(null);
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [hoveredProduct, setHoveredProduct] = useState<SeasonalProduct | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<string>('all');

  // デフォルトの秋の商品データ
  const defaultProducts: SeasonalProduct[] = [
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "綾鷹", "サイズ": "525ml", "価格": 150, "元棚段番号": 1, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["鍋", "おでん", "和惣菜"], "役割": "食中茶", "推奨棚段番号": 2, "推奨フェイス数": 2, "クロスMD": ["ぽん酢", "白だし", "七味唐辛子"], "POP": "秋の鍋に合う食中茶", "配置": "ゴンドラ中央"},
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "綾鷹濃い緑茶", "サイズ": "650ml", "価格": 150, "元棚段番号": 1, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["鍋", "焼き魚"], "役割": "濃い味の食中茶", "推奨棚段番号": 2, "推奨フェイス数": 1, "クロスMD": ["ゆずぽん酢", "だし醤油"], "POP": "脂の乗った秋魚に◎", "配置": "ゴンドラ中央"},
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "爽健美茶", "サイズ": "600ml", "価格": 150, "元棚段番号": 1, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["根菜", "きのこ"], "役割": "さっぱり系食中茶", "推奨棚段番号": 3, "推奨フェイス数": 1, "クロスMD": ["淡口だし", "昆布だし"], "POP": "こっくり料理の口直しに", "配置": "ゴンドラ中央"},
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "やかんの麦茶", "サイズ": "650ml", "価格": 140, "元棚段番号": 1, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["おでん"], "役割": "家族向け食中茶", "推奨棚段番号": 3, "推奨フェイス数": 2, "クロスMD": ["からし", "だしパック"], "POP": "おでんと一緒にまとめ買い", "配置": "エンド"},
    {"小分類": "お茶・紅茶", "メーカー名": "サントリー", "商品名": "伊右衛門", "サイズ": "600ml", "価格": 150, "元棚段番号": 2, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["鍋", "焼き魚"], "役割": "定番食中茶", "推奨棚段番号": 2, "推奨フェイス数": 2, "クロスMD": ["ぽん酢", "すだち果汁"], "POP": "鍋の名脇役", "配置": "ゴンドラ中央"},
    {"小分類": "お茶・紅茶", "メーカー名": "伊藤園", "商品名": "お～いお茶", "サイズ": "600ml", "価格": 150, "元棚段番号": 2, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["和惣菜"], "役割": "定番食中茶", "推奨棚段番号": 2, "推奨フェイス数": 2, "クロスMD": ["めんつゆ", "白だし"], "POP": "煮物に合う緑茶", "配置": "ゴンドラ中央"},
    {"小分類": "お茶・紅茶", "メーカー名": "キリン", "商品名": "午後の紅茶 無糖", "サイズ": "500ml", "価格": 150, "元棚段番号": 2, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["洋風煮込み", "パスタソース"], "役割": "洋惣菜向け食中茶", "推奨棚段番号": 3, "推奨フェイス数": 1, "クロスMD": ["トマトソース", "バジルソース"], "POP": "洋風おかずに無糖ティー", "配置": "ゴンドラ中央"},
    {"小分類": "お茶・紅茶", "メーカー名": "キリン", "商品名": "午後の紅茶 ストレート", "サイズ": "500ml", "価格": 150, "元棚段番号": 2, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["スイーツソース"], "役割": "食後茶", "推奨棚段番号": 4, "推奨フェイス数": 1, "クロスMD": ["はちみつ", "メープルシロップ"], "POP": "デザートとも好相性", "配置": "サイドキャップ"},
    {"小分類": "お茶・紅茶", "メーカー名": "キリン", "商品名": "午後の紅茶 ミルクティー", "サイズ": "500ml", "価格": 150, "元棚段番号": 2, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["焼き菓子", "スイーツソース"], "役割": "食後茶", "推奨棚段番号": 4, "推奨フェイス数": 1, "クロスMD": ["キャラメルソース", "シナモンシュガー"], "POP": "ほっこり秋のティータイム", "配置": "サイドキャップ"},
    {"小分類": "水", "メーカー名": "コカ・コーラ", "商品名": "いろはす", "サイズ": "540ml", "価格": 140, "元棚段番号": 3, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["鍋の素"], "役割": "調理用・口直し", "推奨棚段番号": 5, "推奨フェイス数": 2, "クロスMD": ["鍋つゆ", "濃縮だし"], "POP": "出汁割り・下ごしらえに", "配置": "下段端"},
    {"小分類": "水", "メーカー名": "コカ・コーラ", "商品名": "いろはすマスカット", "サイズ": "540ml", "価格": 140, "元棚段番号": 3, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["スイーツソース"], "役割": "食後・口直し", "推奨棚段番号": 5, "推奨フェイス数": 1, "クロスMD": ["チョコソース", "フルーツソース"], "POP": "濃い味料理のあとに", "配置": "下段端"},
    {"小分類": "水", "メーカー名": "コカ・コーラ", "商品名": "GREEN DAKARA", "サイズ": "500ml", "価格": 140, "元棚段番号": 3, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["根菜", "煮物"], "役割": "ライト甘味の口直し", "推奨棚段番号": 5, "推奨フェイス数": 1, "クロスMD": ["和風だし", "砂糖・みりん"], "POP": "煮物づくり＆ひと休み", "配置": "下段"},
    {"小分類": "水", "メーカー名": "アサヒ飲料", "商品名": "ウィルキンソン炭酸水", "サイズ": "500ml", "価格": 140, "元棚段番号": 3, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["レモン果汁", "ビネガー"], "役割": "割材・口直し", "推奨棚段番号": 4, "推奨フェイス数": 2, "クロスMD": ["レモン果汁ボトル", "飲むお酢"], "POP": "揚げ物後のさっぱりに", "配置": "エンド"},
    {"小分類": "水", "メーカー名": "サントリー", "商品名": "天然水", "サイズ": "500ml", "価格": 140, "元棚段番号": 4, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["鍋の素"], "役割": "調理用定番", "推奨棚段番号": 5, "推奨フェイス数": 2, "クロスMD": ["鍋スープ", "だしパック"], "POP": "鍋は『水』から", "配置": "下段中央"},
    {"小分類": "水", "メーカー名": "サントリー", "商品名": "天然水SPARKLING", "サイズ": "500ml", "価格": 140, "元棚段番号": 4, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["柑橘果汁", "フルーツビネガー"], "役割": "割材・食中", "推奨棚段番号": 4, "推奨フェイス数": 1, "クロスMD": ["ゆず果汁", "りんご酢"], "POP": "さっぱり割りで箸休め", "配置": "サイドキャップ"},
    {"小分類": "スポーツドリンク", "メーカー名": "コカ・コーラ", "商品名": "アクエリアス", "サイズ": "500ml", "価格": 150, "元棚段番号": 4, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["シチュー", "カレー（調理時の水分補給提案）"], "役割": "調理・買い回り同時訴求", "推奨棚段番号": 5, "推奨フェイス数": 1, "クロスMD": ["ルウ各種"], "POP": "煮込みシーズンの水分補給も", "配置": "下段端"},
    {"小分類": "スポーツドリンク", "メーカー名": "大塚製薬", "商品名": "ポカリスエット", "サイズ": "500ml", "価格": 150, "元棚段番号": 4, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["煮込み"], "役割": "買い回り補完", "推奨棚段番号": 5, "推奨フェイス数": 1, "クロスMD": ["シチュールウ", "ブイヨン"], "POP": "長時間調理のパートナー", "配置": "下段端"},
    {"小分類": "スポーツドリンク", "メーカー名": "アサヒ飲料", "商品名": "カルピスソーダ", "サイズ": "500ml", "価格": 150, "元棚段番号": 4, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["デザートソース"], "役割": "食後・子ども向け", "推奨棚段番号": 4, "推奨フェイス数": 1, "クロスMD": ["チョコソース", "いちごソース"], "POP": "食後のごほうび", "配置": "サイドキャップ"},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "コカ・コーラ", "サイズ": "500ml", "価格": 150, "元棚段番号": 5, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["BBQソース", "照り焼きタレ"], "役割": "こってり料理の相棒", "推奨棚段番号": 3, "推奨フェイス数": 2, "クロスMD": ["BBQソース", "焼肉のタレ"], "POP": "濃い味×強炭酸で満足感", "配置": "エンド"},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "コカ･コーラゼロ", "サイズ": "500ml", "価格": 150, "元棚段番号": 5, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["ヘルシー調味"], "役割": "低糖訴求の相棒", "推奨棚段番号": 3, "推奨フェイス数": 1, "クロスMD": ["減塩だし", "糖質オフソース"], "POP": "『罪悪感オフ』提案", "配置": "エンド"},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "ファンタオレンジ", "サイズ": "500ml", "価格": 150, "元棚段番号": 5, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["マーマレード", "砂糖"], "役割": "子ども向け・食後", "推奨棚段番号": 4, "推奨フェイス数": 1, "クロスMD": ["マーマレード", "パンケーキシロップ"], "POP": "家族で楽しい味", "配置": "サイドキャップ"},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "ファンタグレープ", "サイズ": "500ml", "価格": 150, "元棚段番号": 5, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["バルサミコ酢", "フルーツソース"], "役割": "食後・デザート寄り", "推奨棚段番号": 4, "推奨フェイス数": 1, "クロスMD": ["バルサミコ酢", "ぶどうソース"], "POP": "果実感で食後満足", "配置": "サイドキャップ"},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "カナダドライジンジャーエール", "サイズ": "500ml", "価格": 150, "元棚段番号": 5, "コーナー": "調味料", "シーズン": "秋", "週": "10月第1週", "調味料テーマ": ["生姜", "から揚げ粉"], "役割": "揚げ物の口直し", "推奨棚段番号": 3, "推奨フェイス数": 1, "クロスMD": ["おろし生姜チューブ", "から揚げ粉"], "POP": "生姜×炭酸でさっぱり", "配置": "エンド"}
  ];

  const displayProducts = products.length > 0 ? products : defaultProducts;

  // 棚段ごとに商品をグループ化（推奨棚段番号を使用）
  const productsByShelf = useMemo(() => {
    const grouped: { [key: number]: SeasonalProduct[] } = {};
    for (let i = 1; i <= 5; i++) {
      grouped[i] = [];
    }
    
    // フェイス数に応じて商品を複製
    displayProducts.forEach(product => {
      for (let i = 0; i < product.推奨フェイス数; i++) {
        grouped[product.推奨棚段番号].push(product);
      }
    });
    
    return grouped;
  }, [displayProducts]);

  // 調味料テーマの一覧を取得
  const allThemes = useMemo(() => {
    const themes = new Set<string>();
    displayProducts.forEach(p => {
      p.調味料テーマ.forEach(theme => themes.add(theme));
    });
    return Array.from(themes);
  }, [displayProducts]);

  // 商品の色を取得
  const getProductColor = (product: SeasonalProduct): { bg: string; text: string } => {
    const categoryColors = AUTUMN_COLORS[product.小分類 as keyof typeof AUTUMN_COLORS];
    if (categoryColors?.商品別) {
      const productColor = Object.entries(categoryColors.商品別).find(([key]) => 
        product.商品名.includes(key)
      );
      if (productColor) {
        return productColor[1];
      }
    }
    // デフォルトカラー
    return { bg: categoryColors?.base || '#808080', text: 'white' };
  };

  // 商品名を短縮表示（改行対応）
  const formatProductName = (name: string): string[] => {
    const shortNames: { [key: string]: string } = {
      '綾鷹濃い緑茶': '綾鷹\n濃い緑茶',
      '午後の紅茶 無糖': '午後の紅茶\n無糖',
      '午後の紅茶 ストレート': '午後の紅茶\nストレート',
      '午後の紅茶 ミルクティー': '午後の紅茶\nミルクティー',
      'いろはすマスカット': 'いろはす\nマスカット',
      '天然水SPARKLING': '天然水\nSPARKLING',
      'ウィルキンソン炭酸水': 'ウィルキンソン\n炭酸水',
      'カナダドライジンジャーエール': 'カナダドライ\nジンジャー',
      'コカ･コーラゼロ': 'コカ・コーラ\nゼロ',
      'ファンタオレンジ': 'ファンタ\nオレンジ',
      'ファンタグレープ': 'ファンタ\nグレープ',
    };
    const formatted = shortNames[name] || name;
    return formatted.split('\n');
  };

  // 商品カードのレンダリング
  const renderProduct = (product: SeasonalProduct, index: number) => {
    const isSelected = selectedProduct === product;
    const isHovered = hoveredProduct === product;
    const productColor = getProductColor(product);
    const nameLines = formatProductName(product.商品名);
    const hasTheme = selectedTheme === 'all' || product.調味料テーマ.includes(selectedTheme);

    return (
      <Tooltip
        key={`${product.商品名}-${index}`}
        content={
          <div style={{ fontSize: '0.85rem', padding: spacing.xs, maxWidth: '300px' }}>
            <div style={{ fontWeight: 700, marginBottom: spacing.xs, fontSize: '1rem' }}>
              {product.商品名} {product.サイズ}
            </div>
            <div style={{ marginBottom: spacing.xs }}>
              <strong>メーカー:</strong> {product.メーカー名}
            </div>
            <div style={{ marginBottom: spacing.xs }}>
              <strong>価格:</strong> ¥{product.価格}
            </div>
            <div style={{ marginBottom: spacing.xs }}>
              <strong>役割:</strong> {product.役割}
            </div>
            <div style={{ marginBottom: spacing.xs }}>
              <strong>調味料テーマ:</strong> {product.調味料テーマ.join('、')}
            </div>
            <div style={{ marginBottom: spacing.xs }}>
              <strong>クロスMD:</strong> {product.クロスMD.join('、')}
            </div>
            <div style={{ 
              backgroundColor: '#FFF3E0', 
              padding: spacing.xs, 
              borderRadius: borderRadius.sm,
              marginTop: spacing.xs 
            }}>
              <strong>POP:</strong> {product.POP}
            </div>
          </div>
        }
      >
        <div
          className="product-card"
          onClick={() => {
            setSelectedProduct(product);
            onProductClick?.(product);
          }}
          onMouseEnter={() => setHoveredProduct(product)}
          onMouseLeave={() => setHoveredProduct(null)}
          style={{
            width: '85px',
            height: '130px',
            backgroundColor: productColor.bg,
            border: `2px solid ${isSelected ? colors.accent.purple : isHovered ? colors.accent.blue : hasTheme ? '#FFD700' : 'white'}`,
            borderRadius: borderRadius.md,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            transform: isHovered ? 'scale(1.08) translateY(-2px)' : 'scale(1)',
            boxShadow: isSelected ? shadows.xl : isHovered ? shadows.lg : shadows.md,
            padding: spacing.xs,
            position: 'relative',
            color: productColor.text,
            fontWeight: 600,
            fontSize: '0.7rem',
            opacity: selectedTheme !== 'all' && !hasTheme ? 0.4 : 1,
          }}
        >
          {/* 配置アイコン */}
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              boxShadow: shadows.sm,
              border: `1px solid ${colors.ui.border}`,
            }}
          >
            {PLACEMENT_ICONS[product.配置] || '📍'}
          </div>

          {/* 調味料テーマバッジ */}
          {product.調味料テーマ[0] && (
            <div
              style={{
                position: 'absolute',
                top: '4px',
                left: '4px',
                backgroundColor: THEME_COLORS[product.調味料テーマ[0]] || '#666',
                color: 'white',
                fontSize: '0.45rem',
                padding: '1px 3px',
                borderRadius: '3px',
                fontWeight: 700,
                maxWidth: '40px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {product.調味料テーマ[0]}
            </div>
          )}

          {/* 商品名 */}
          <div style={{ 
            marginTop: product.調味料テーマ[0] ? '20px' : '8px',
            textAlign: 'center',
            lineHeight: 1.1,
          }}>
            {nameLines.map((line, i) => (
              <div key={i} style={{ fontSize: i === 0 ? '0.75rem' : '0.65rem' }}>
                {line}
              </div>
            ))}
          </div>

          {/* サイズ */}
          <div style={{
            fontSize: '0.95rem',
            fontWeight: 700,
            textShadow: productColor.text === 'white' ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
          }}>
            {product.サイズ}
          </div>

          {/* 価格とPOPメッセージ */}
          <div style={{
            width: '100%',
            backgroundColor: 'rgba(255,255,255,0.95)',
            color: colors.text.primary,
            fontSize: '0.55rem',
            padding: '3px',
            borderRadius: '0 0 6px 6px',
            textAlign: 'center',
            borderTop: `1px solid ${colors.ui.border}`,
          }}>
            <div style={{ fontWeight: 700 }}>¥{product.価格}</div>
            <div style={{ 
              fontSize: '0.45rem', 
              color: colors.text.secondary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {product.POP}
            </div>
          </div>
        </div>
      </Tooltip>
    );
  };

  return (
    <Card className="seasonal-crossmd-visualizer">
      {/* ヘッダー */}
      <div style={{
        padding: spacing.md,
        borderBottom: `1px solid ${colors.ui.border}`,
        background: `linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            <Calendar size={24} color={colors.accent.orange} />
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text.primary }}>
              秋の調味料クロスMD提案
            </h3>
            <Badge variant="warning">10月第1週</Badge>
          </div>
          <div style={{ display: 'flex', gap: spacing.sm }}>
            <Button
              size="small"
              variant={viewMode === 'seasonal' ? 'primary' : 'secondary'}
              onClick={() => setViewMode('seasonal')}
            >
              <Sparkles size={14} />
              季節提案
            </Button>
            <Button
              size="small"
              variant={viewMode === 'category' ? 'primary' : 'secondary'}
              onClick={() => setViewMode('category')}
            >
              <Package size={14} />
              カテゴリー
            </Button>
            <Button
              size="small"
              variant={viewMode === 'crossmd' ? 'primary' : 'secondary'}
              onClick={() => setViewMode('crossmd')}
            >
              <ShoppingCart size={14} />
              クロスMD
            </Button>
          </div>
        </div>

        {/* 調味料テーマフィルター */}
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <span style={{ fontSize: '0.875rem', color: colors.text.secondary }}>
            調味料テーマ:
          </span>
          <select
            value={selectedTheme}
            onChange={(e) => setSelectedTheme(e.target.value)}
            style={{
              padding: '4px 8px',
              borderRadius: borderRadius.sm,
              border: `1px solid ${colors.ui.border}`,
              fontSize: '0.875rem',
            }}
            aria-label="調味料テーマ選択"
          >
            <option value="all">すべて表示</option>
            {allThemes.map(theme => (
              <option key={theme} value={theme}>{theme}</option>
            ))}
          </select>
        </div>
      </div>

      {/* 棚割り表示エリア */}
      <div style={{
        padding: spacing.lg,
        background: `linear-gradient(180deg, #FFF8E1 0%, #FFFFFF 100%)`,
        minHeight: '650px',
      }}>
        {/* 秋の装飾 */}
        <div style={{
          textAlign: 'center',
          marginBottom: spacing.md,
          fontSize: '1.5rem',
        }}>
          🍁 🍄 🌰 🍂 🎃 🍄 🌰 🍂 🍁
        </div>

        {/* 棚の外枠 */}
        <div style={{
          backgroundColor: 'white',
          border: `3px solid ${colors.ui.borderStrong}`,
          borderRadius: borderRadius.lg,
          padding: spacing.md,
          boxShadow: shadows.xl,
          background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%)',
        }}>
          {/* 各棚段の表示 */}
          {[1, 2, 3, 4, 5].map((shelfNumber) => (
            <div key={shelfNumber} style={{ marginBottom: shelfNumber < 5 ? spacing.md : 0 }}>
              {/* 棚段情報 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: spacing.xs,
              }}>
                <div style={{
                  backgroundColor: shelfNumber === 2 || shelfNumber === 3 ? '#FFD700' : colors.ui.bgTertiary,
                  color: shelfNumber === 2 || shelfNumber === 3 ? colors.text.primary : colors.text.secondary,
                  padding: '4px 12px',
                  borderRadius: borderRadius.sm,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  marginRight: spacing.sm,
                  boxShadow: shelfNumber === 2 || shelfNumber === 3 ? shadows.sm : 'none',
                }}>
                  {shelfNumber}段
                  {shelfNumber === 2 && ' 🌟'}
                  {shelfNumber === 3 && ' ⭐'}
                </div>
                {shelfNumber === 2 && (
                  <Badge variant="success" style={{ marginRight: spacing.sm }}>
                    ゴールデンゾーン
                  </Badge>
                )}
                <div style={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: colors.ui.border,
                }}></div>
              </div>

              {/* 商品配置エリア */}
              <div style={{
                display: 'flex',
                gap: spacing.xs,
                padding: spacing.sm,
                backgroundColor: shelfNumber === 2 || shelfNumber === 3 
                  ? 'linear-gradient(90deg, #FFFEF7 0%, #FFF9E6 100%)' 
                  : colors.ui.bgSecondary,
                borderRadius: borderRadius.md,
                minHeight: '150px',
                alignItems: 'center',
                overflowX: 'auto',
                borderBottom: `3px solid ${colors.ui.borderStrong}`,
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
              }}>
                {productsByShelf[shelfNumber].length > 0 ? (
                  productsByShelf[shelfNumber].map((product, index) => 
                    renderProduct(product, index)
                  )
                ) : (
                  <div style={{
                    flex: 1,
                    textAlign: 'center',
                    color: colors.text.tertiary,
                    fontSize: '0.875rem',
                  }}>
                    商品なし
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* 床面表示 */}
          <div style={{
            height: '25px',
            background: `linear-gradient(to bottom, ${colors.ui.borderStrong}, ${colors.ui.bgTertiary})`,
            borderRadius: `0 0 ${borderRadius.md} ${borderRadius.md}`,
            marginTop: spacing.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            color: colors.text.tertiary,
          }}>
            調味料コーナー × 飲料 クロスマーチャンダイジング
          </div>
        </div>
      </div>

      {/* 選択商品の詳細情報 */}
      {selectedProduct && (
        <div style={{
          padding: spacing.lg,
          borderTop: `2px solid ${colors.ui.border}`,
          backgroundColor: '#FFF8E1',
        }}>
          <h4 style={{ fontWeight: 700, marginBottom: spacing.md, color: colors.text.primary }}>
            商品詳細とクロスMD提案
          </h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg }}>
            {/* 左側：商品情報 */}
            <div>
              <div style={{ marginBottom: spacing.sm }}>
                <strong>商品名:</strong> {selectedProduct.商品名} {selectedProduct.サイズ}
              </div>
              <div style={{ marginBottom: spacing.sm }}>
                <strong>メーカー:</strong> {selectedProduct.メーカー名}
              </div>
              <div style={{ marginBottom: spacing.sm }}>
                <strong>価格:</strong> ¥{selectedProduct.価格}
              </div>
              <div style={{ marginBottom: spacing.sm }}>
                <strong>役割:</strong> {selectedProduct.役割}
              </div>
              <div style={{ marginBottom: spacing.sm }}>
                <strong>推奨配置:</strong> {selectedProduct.配置} {PLACEMENT_ICONS[selectedProduct.配置]}
              </div>
            </div>
            
            {/* 右側：クロスMD情報 */}
            <div>
              <div style={{ marginBottom: spacing.sm }}>
                <strong>調味料テーマ:</strong>
                <div style={{ display: 'flex', gap: spacing.xs, marginTop: spacing.xs }}>
                  {selectedProduct.調味料テーマ.map(theme => (
                    <Badge 
                      key={theme}
                      style={{ 
                        backgroundColor: THEME_COLORS[theme] || '#666',
                        color: 'white' 
                      }}
                    >
                      {theme}
                    </Badge>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: spacing.sm }}>
                <strong>クロスMD商品:</strong>
                <div style={{ marginTop: spacing.xs }}>
                  {selectedProduct.クロスMD.map((item, i) => (
                    <span key={i}>
                      {i > 0 && '、'}
                      <span style={{ 
                        backgroundColor: colors.accent.blue + '20',
                        padding: '2px 6px',
                        borderRadius: borderRadius.sm,
                        marginRight: spacing.xs,
                      }}>
                        {item}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
              <div style={{
                backgroundColor: '#FFE0B2',
                padding: spacing.sm,
                borderRadius: borderRadius.md,
                marginTop: spacing.sm,
              }}>
                <strong>POPメッセージ:</strong>
                <div style={{ fontSize: '1.1rem', marginTop: spacing.xs }}>
                  「{selectedProduct.POP}」
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default SeasonalCrossMDVisualizer;
